import React, { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { LAYOFF_LOCK_EVENT } from '_helpers/layoffLock';
import EmployeeNotice from './EmployeeNotice';
import { noticeKeys, useActiveNotice } from './useNotice';
import styles from '../layoff.module.scss';

const logOut = () => {
  localStorage.removeItem('user');
  window.location.assign('/login');
};

/**
 * Once a superintendent signs an employee's lay-off notice, the employee can only open that
 * notice (and log out) until they sign it. While that is the case this renders the notice in a
 * minimal shell instead of the app, so none of the app's other API calls run (the API refuses
 * them with 423 anyway).
 */
const LayoffLockGate = ({ children }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useActiveNotice();

  // Any API call answered with 423 means a superintendent just signed: re-check and lock
  useEffect(() => {
    const recheck = () => queryClient.invalidateQueries(noticeKeys.active);
    window.addEventListener(LAYOFF_LOCK_EVENT, recheck);
    return () => window.removeEventListener(LAYOFF_LOCK_EVENT, recheck);
  }, [queryClient]);

  // Stay closed until the first check answers, so a locked employee never sees the app.
  // App.jsx only wraps employees in this gate, so other users never wait on this check.
  if (isLoading) {
    return (
      <div className={styles.page_message} role='status' aria-live='polite'>
        <img
          alt='Loading'
          src={require('../../assets/images/loader.gif').default}
        />
      </div>
    );
  }

  // If the check itself fails, don't block the app: the API still enforces the lock (423),
  // which triggers a re-check here
  if (!data?.locked) {
    return children;
  }

  return (
    <div className={styles.lock_shell}>
      <header className={styles.lock_header}>
        <span className={styles.lock_brand}>Firestorm</span>
        <button type='button' className='button outlined' onClick={logOut}>
          Log out
        </button>
      </header>
      <main className={styles.lock_main}>
        <EmployeeNotice
          hash={data.layoff.hash}
          onDone={() => queryClient.invalidateQueries(noticeKeys.active)}
        />
      </main>
    </div>
  );
};

export default LayoffLockGate;
